// frontend/src/components/Common/Comments/CommentForm.jsx
//
// Changes:
//  - Private/public toggle hidden (always public)
//  - Destination user → multi-user "Notificar utilizadores" with SearchableSelect chips
//  - Textarea → RichTextEditor (handles image paste natively)
//  - destinationUserId removed — mentionedUserIds is now the single source
//    of truth on the backend (core.comments.mentioned_user_ids)

import React, { useState, useEffect } from 'react';
import { commentsAPI, attachmentsAPI } from '../../../api/client';
import { useTranslation } from '../../../context/TranslationContext';
import RichTextEditor from '../RichTextEditor';
import SearchableSelect from '../SearchableSelect';
import { FiSend, FiPaperclip, FiX } from 'react-icons/fi';
import './Comments.css';

const CommentForm = ({
  entityType, entityId,
  subEntityType = null, subEntityId = null, subEntityLabel = null,  // optional task reference
  onCommentAdded, onError,
}) => {
  const { t } = useTranslation();
  const [commentHtml,    setCommentHtml]    = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [pendingFiles,   setPendingFiles]   = useState([]);
  const [submitting,     setSubmitting]     = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    commentsAPI.getAvailableUsers()
      .then(r => { if (r.data.success) setAvailableUsers(r.data.data); })
      .catch(console.error);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (pendingFiles.length + files.length > 5) {
      onError?.(t('comments.max_files', 'Maximum 5 files per comment')); return;
    }
    if (files.some(f => f.size > 10 * 1024 * 1024)) {
      onError?.(t('comments.file_too_large', 'File too large. Max 10MB.')); return;
    }
    setPendingFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const plain = commentHtml.replace(/<[^>]*>/g, '').trim();
    if (!plain && pendingFiles.length === 0) return;
    try {
      setSubmitting(true);
      const response = await commentsAPI.create({
        entityType, entityId,
        commentText:      commentHtml,
        isPublic:         true,
        mentionedUserIds:  mentionedUsers,
        images:            [],
        subEntityType,
        subEntityId,
        subEntityLabel,
      });
      if (response.data.success) {
        const newComment = response.data.data;
        if (pendingFiles.length > 0) {
          try {
            await attachmentsAPI.upload('comment', newComment.comment_id, pendingFiles,
              `comment_${newComment.comment_id}`);
          } catch (uploadErr) {
            onError?.(t('comments.upload_error', 'Comment added but files failed to upload'));
          }
        }
        setCommentHtml('');
        setMentionedUsers([]);
        setPendingFiles([]);
        onCommentAdded?.(newComment);
      }
    } catch (err) {
      onError?.(t('comments.error_create', 'Failed to add comment'));
    } finally {
      setSubmitting(false);
    }
  };

  const getUserName = (uid) =>
    availableUsers.find(u => Number(u.user_id) === Number(uid))?.full_name || String(uid);

  return (
    <div className="comment-form">

      {/* Notify users */}
      <div className="comment-form-notify">
        <SearchableSelect
          value=""
          onChange={id => {
            if (id && !mentionedUsers.includes(Number(id)))
              setMentionedUsers(prev => [...prev, Number(id)]);
          }}
          options={availableUsers.filter(u => !mentionedUsers.includes(Number(u.user_id)))}
          getOptionLabel={u => u.full_name || u.username}
          getOptionValue={u => u.user_id}
          placeholder={t('comments.notify_users', '+ Notificar utilizadores')}
        />
        {mentionedUsers.length > 0 && (
          <div className="comment-mentioned-chips">
            {mentionedUsers.map(uid => (
              <span key={uid} className="comment-mention-chip">
                👤 {getUserName(uid)}
                <button type="button"
                  onClick={() => setMentionedUsers(p => p.filter(id => Number(id) !== Number(uid)))}>
                  <FiX size={11}/>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Rich text editor */}
      <RichTextEditor
        value={commentHtml}
        onChange={setCommentHtml}
        placeholder={t('comments.placeholder', 'Escreva um comentário… (Ctrl+V para colar imagens)')}
        minHeight={130}
      />

      {/* Pending file chips */}
      {pendingFiles.length > 0 && (
        <div className="file-attachments-preview">
          {pendingFiles.map((file, i) => (
            <div key={i} className="file-preview-item">
              <FiPaperclip size={13}/>
              <span className="file-name">{file.name}</span>
              <span className="file-size">({(file.size/1024).toFixed(0)} KB)</span>
              <button type="button" className="remove-file-btn"
                onClick={() => setPendingFiles(p => p.filter((_,j) => j !== i))}>
                <FiX size={13}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="comment-form-actions">
        <div className="left-actions">
          <input ref={fileInputRef} type="file" multiple style={{ display:'none' }}
            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
            onChange={handleFileSelect}/>
          <button type="button" className="btn-attach"
            title={t('comments.attach_file', 'Attach file')}
            disabled={submitting || pendingFiles.length >= 5}
            onClick={() => fileInputRef.current?.click()}>
            <FiPaperclip size={17}/>
          </button>
          {pendingFiles.length > 0 && (
            <span className="help-text">
              {pendingFiles.length} {t('comments.files', 'ficheiro(s)')}
            </span>
          )}
        </div>
        <button type="button" className="btn-send-comment"
          title={t('comments.send', 'Enviar')}
          disabled={(!commentHtml.replace(/<[^>]*>/g,'').trim() && !pendingFiles.length) || submitting}
          onClick={handleSubmit}>
          <FiSend size={17}/>
        </button>
      </div>
    </div>
  );
};

export default CommentForm;